import type { IBuffer, Terminal } from "@xterm/xterm";

export const TERMINAL_COMMAND_MAX_BYTES = 64 * 1024;

const commandEncoder = new TextEncoder();

export type TerminalOutputChunk = { text: string; color: "default" | "error" | "muted" };

export type TerminalCommandOutcome =
	| { type: "exit"; exitCode: number }
	| { type: "error"; message: string };

/**
 * コマンド終了時に画面と転写へ追加する出力を整形する。
 * シェルの終了コードは診断済みのPTY出力に重複して表示しない。
 */
export function terminalCommandOutcomeChunks(
	outcome: TerminalCommandOutcome,
): TerminalOutputChunk[] {
	if (outcome.type === "exit") return [];
	return [{ text: `${outcome.message}\n`, color: "error" }];
}

/**
 * DECSC を前置し、alt screen 未使用コマンドでは ?1049l のカーソル復元 (DECRC 相当、未保存時は原点へ飛ぶ) を無害化する。
 * alt screen 放置終了時は ?1049h 時に通常バッファ側へ保存された位置が復元されるため復旧動作は維持される。
 */
export const COMMAND_SCREEN_RESET = "\u001b7\u001b[?1049l\u001b[!p\u001b[?2004h";

/** プロンプト再描画前に改行が要るか。行頭でも文字が残っている行 (CR 終端の進捗表示等) は潰さない。 */
function needsPromptLeadingNewline(buffer: IBuffer): boolean {
	if (buffer.cursorX !== 0) return true;
	const line = buffer.getLine(buffer.baseY + buffer.cursorY);
	return (line?.translateToString(true) ?? "") !== "";
}

/**
 * コマンド実行が残した端末状態を戻し、反映後のカーソル位置を見て必要なら改行を補う。
 * xterm の write は非同期にパースされるため、カーソルは完了コールバックの中でしか正しく読めない。
 */
export function resetCommandScreen(
	terminal: Terminal,
	onReset: (trailingNewline: string) => void,
): void {
	terminal.write(COMMAND_SCREEN_RESET, () => {
		const trailingNewline = needsPromptLeadingNewline(terminal.buffer.active) ? "\r\n" : "";
		if (trailingNewline !== "") terminal.write(trailingNewline);
		onReset(trailingNewline);
	});
}

export interface TerminalOutputScrollShortcutState {
	key: string;
	ctrlKey: boolean;
	metaKey: boolean;
	shiftKey: boolean;
	altKey: boolean;
	isComposing: boolean;
}

export type TerminalOutputScrollDirection = "up" | "down";

export function getTerminalOutputScrollDirection(
	state: TerminalOutputScrollShortcutState,
): TerminalOutputScrollDirection | null {
	if (state.ctrlKey || state.metaKey || state.shiftKey || state.altKey || state.isComposing) {
		return null;
	}
	if (state.key === "PageUp") return "up";
	if (state.key === "PageDown") return "down";
	return null;
}

export interface TerminalMetaArrowKeyState {
	key: string;
	ctrlKey: boolean;
	metaKey: boolean;
	shiftKey: boolean;
	altKey: boolean;
	isComposing: boolean;
	/** DECCKM (アプリケーションカーソルキーモード)。vim 等が有効化する。 */
	applicationCursorKeys: boolean;
}

/**
 * cmd+←/→ を Home/End シーケンスへ変換する。
 * xterm.js は metaKey 付き矢印を無視し、そのままではブラウザの戻る/進むに取られるため。
 * DECCKM 有効時は実キーボードの Home/End と同じく SS3 形式で送る。
 */
export function getTerminalMetaArrowSequence(state: TerminalMetaArrowKeyState): string | null {
	if (!state.metaKey || state.ctrlKey || state.shiftKey || state.altKey || state.isComposing) {
		return null;
	}
	if (state.key === "ArrowLeft") return state.applicationCursorKeys ? "\u001bOH" : "\u001b[H";
	if (state.key === "ArrowRight") return state.applicationCursorKeys ? "\u001bOF" : "\u001b[F";
	return null;
}

export interface TerminalCopyKeyState {
	key: string;
	ctrlKey: boolean;
	metaKey: boolean;
	altKey: boolean;
	isComposing: boolean;
	hasSelection: boolean;
}

/** Ctrl+C (Win/Linux) または Cmd+C (macOS) と選択範囲でコピーする。 */
export function shouldCopyTerminalSelection(state: TerminalCopyKeyState): boolean {
	return (
		state.hasSelection &&
		state.key.toLowerCase() === "c" &&
		state.ctrlKey !== state.metaKey &&
		!state.altKey &&
		!state.isComposing
	);
}

export function validateTerminalCommand(command: string): string | null {
	if (command.trim().length === 0) return "コマンドを入力してください。";
	if (command.includes("\0")) return "コマンドにNUL文字は使用できません。";
	// UTF-16 の 1 コードユニットは最大 3 バイト (サロゲートペアは 2 ユニットで 4 バイト) なので、
	// 上限を超えられない短い文字列ではエンコードを省く。
	if (
		command.length * 3 > TERMINAL_COMMAND_MAX_BYTES &&
		commandEncoder.encode(command).byteLength > TERMINAL_COMMAND_MAX_BYTES
	) {
		return "コマンドはUTF-8で64KiB以内にしてください。";
	}
	return null;
}

export function formatTerminalCwd(cwd: string | null | undefined): string {
	if (!cwd || cwd === "/root") return "~";
	if (cwd.startsWith("/root/")) return `~${cwd.slice("/root".length)}`;
	return cwd;
}

/** 作業ディレクトリを `~/work$ ` のようなターミナルのプロンプト文字列に整形する。 */
export function formatTerminalPrompt(cwd: string | null | undefined): string {
	return `${formatTerminalCwd(cwd)}$ `;
}

export function terminalContextKey(chatId: string | null, environmentId: string | null): string {
	return `${chatId ?? "new"}:${environmentId ?? "none"}`;
}

export interface TerminalContextIdentity {
	chatId: string | null;
	environmentId: string | null;
	newChatEnvironmentId: string | null;
}

export type TerminalContextTransition = "unchanged" | "draft-promoted" | "changed";

function effectiveTerminalEnvironmentId(context: TerminalContextIdentity): string | null {
	return context.chatId === null ? context.newChatEnvironmentId : context.environmentId;
}

/**
 * 新規チャット下書きへのサーバー ID 付与だけを、同じターミナル文脈の昇格として判定する。
 * 環境や既存チャットの切替は通常のコンテキスト変更として扱う。
 */
export function terminalContextTransition(
	previous: TerminalContextIdentity,
	next: TerminalContextIdentity,
): TerminalContextTransition {
	const previousEnvironmentId = effectiveTerminalEnvironmentId(previous);
	const nextEnvironmentId = effectiveTerminalEnvironmentId(next);
	if (previous.chatId === next.chatId && previousEnvironmentId === nextEnvironmentId) {
		return "unchanged";
	}
	if (
		previous.chatId === null &&
		next.chatId !== null &&
		previous.newChatEnvironmentId !== null &&
		previous.newChatEnvironmentId === previousEnvironmentId &&
		previousEnvironmentId === nextEnvironmentId &&
		next.newChatEnvironmentId === nextEnvironmentId
	) {
		return "draft-promoted";
	}
	return "changed";
}

/**
 * 確定済みの別環境へ切り替わったときだけ保存済みターミナル状態を破棄する。
 * 新規チャット下書き遷移中の一時的な null は環境変更とみなさない。
 */
export function shouldDiscardStoredTerminalState(
	previous: TerminalContextIdentity,
	nextEnvironmentId: string | null,
): boolean {
	const previousEnvironmentId = effectiveTerminalEnvironmentId(previous);
	return (
		previousEnvironmentId !== null &&
		nextEnvironmentId !== null &&
		previousEnvironmentId !== nextEnvironmentId
	);
}

export interface TerminalPersistIdentity {
	userId: string;
	environmentId: string | null;
	chatId: string | null;
}

/** タブ状態を読み込んだ identity と現在の identity が一致するときだけ保存を許可する。 */
export function canPersistTerminalTabs(
	hydrated: TerminalPersistIdentity,
	current: TerminalPersistIdentity,
): boolean {
	return (
		current.userId !== "" &&
		current.environmentId !== null &&
		hydrated.userId === current.userId &&
		hydrated.environmentId === current.environmentId &&
		hydrated.chatId === current.chatId
	);
}
